export interface LegacyFooterConfigLike {
  [key: string]: any;
}

export function isLegacyFooterConfig(value: unknown): value is LegacyFooterConfigLike {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const candidate = value as LegacyFooterConfigLike;
  return Array.isArray(candidate.columns)
    && (typeof candidate.bg_color === 'string' || typeof candidate.padding_top === 'number');
}

function textLinks(links: any[]): string {
  return links
    .filter((link) => link && typeof link.label === 'string')
    .map((link) => `<p><a href="${String(link.href || '#')}">${link.label}</a></p>`)
    .join('');
}

function columnChildren(column: any, columnIndex: number, titleColor: string, textColor: string): any[] {
  const children: any[] = [];
  for (const [blockIndex, item] of (Array.isArray(column?.blocks) ? column.blocks : []).entries()) {
    const id = `legacy-footer-${columnIndex}-${blockIndex}`;
    if (item?.title) {
      children.push({
        id: `${id}-title`,
        type: 'header',
        content: String(item.title),
        settings: { level: 'h3' },
        styles: { color: titleColor, fontSize: 15, fontWeight: 800, marginBottom: 14 },
      });
    }

    if (item?.type === 'links') {
      children.push({
        id: `${id}-links`,
        type: 'text',
        content: textLinks(Array.isArray(item.links) ? item.links : []),
        settings: {},
        styles: { color: textColor, fontSize: 14, lineHeight: 1.85 },
      });
    } else if (item?.type === 'contact') {
      const contact = [
        item.phone ? `<p><a href="tel:${String(item.phone).replace(/\s+/g, '')}">${item.phone}</a></p>` : '',
        item.email ? `<p><a href="mailto:${item.email}">${item.email}</a></p>` : '',
      ].join('');
      children.push({ id: `${id}-contact`, type: 'text', content: contact, settings: {}, styles: { color: textColor, fontSize: 14, lineHeight: 1.85 } });
    } else if (item?.type === 'social') {
      const links = (Array.isArray(item.links) ? item.links : [])
        .map((link: any) => `<a href="${String(link.href || '#')}">${String(link.platform || 'Profil')}</a>`)
        .join(' · ');
      if (links) children.push({ id: `${id}-social`, type: 'text', content: links, settings: {}, styles: { color: textColor, fontSize: 13, marginTop: 18 } });
    } else if (item?.content) {
      children.push({ id: `${id}-text`, type: 'text', content: String(item.content), settings: {}, styles: { color: textColor, fontSize: 14, lineHeight: 1.7 } });
    }
  }
  return children;
}

/** Convert the legacy Appearance footer JSON into ordinary, fully editable builder blocks. */
export function convertLegacyFooterToBlocks(raw: LegacyFooterConfigLike): any[] {
  const backgroundColor = raw.bg_color || '#0f172a';
  const textColor = raw.text_color || '#cbd5e1';
  const titleColor = raw.title_color || '#ffffff';
  const columns = Array.isArray(raw.columns) ? raw.columns : [];

  const content: any[] = [{
    id: 'globals',
    type: 'globals',
    settings: {
      fonts: ["'Onest Variable', sans-serif", "'Geist Variable', sans-serif"],
      colors: ['#d85a24', '#1f2933', '#256b8a', '#f3eee5', '#ffffff'],
      pageBackground: backgroundColor,
      pageTextColor: textColor,
      legacyFooterConfig: raw,
    },
  }];

  content.push({
    id: 'legacy-footer-main',
    type: 'row',
    customName: 'Stopka główna',
    settings: {
      columns: Math.max(1, columns.length || 4),
      mobileColumns: 1,
      columnWidths: columns.length === 4 ? [27, 25, 25, 23] : undefined,
      gap: Number(raw.columns_gap || 36),
      alignItems: 'start',
    },
    styles: {
      backgroundColor,
      color: textColor,
      paddingTop: Number(raw.padding_top || 56),
      paddingBottom: Number(raw.padding_bottom || 36),
      borderWidth: Number(raw.border_width || 0),
      borderColor: raw.border_color || 'transparent',
    },
    children: columns.map((column: any, columnIndex: number) => ({
      id: `legacy-footer-column-${columnIndex}`,
      type: 'container',
      customName: `Kolumna ${columnIndex + 1}`,
      settings: {},
      styles: { padding: Number(column?.padding || 0) },
      children: columnChildren(column, columnIndex, titleColor, textColor),
    })),
  });

  if (raw.bar) {
    content.push({
      id: 'legacy-footer-bar',
      type: 'row',
      customName: 'Pasek prawny',
      settings: { columns: 1, mobileColumns: 1, alignItems: 'center' },
      styles: {
        backgroundColor: raw.bar.bg_color || backgroundColor,
        color: raw.bar.text_color || textColor,
        paddingTop: Number(raw.bar.padding_y || 16),
        paddingBottom: Number(raw.bar.padding_y || 16),
        borderTopWidth: 1,
        borderColor: raw.border_color || 'rgba(255,255,255,0.08)',
      },
      children: [{
        id: 'legacy-footer-bar-column',
        type: 'container',
        settings: {},
        styles: {},
        children: [{
          id: 'legacy-footer-copyright',
          type: 'text',
          content: String(raw.bar.copyright_text || '© {year} {site_name}. Wszelkie prawa zastrzeżone.'),
          settings: {},
          styles: { color: raw.bar.text_color || textColor, fontSize: 12, textAlign: raw.bar.alignment || 'left' },
        }],
      }],
    });
  }

  return content;
}
